/** Comma-separated member emails from search/API shared portfolio rows. */
export function parseSharedPortfolioMemberEmails(members: string | null | undefined): string[] {
  if (!members) return []
  return String(members)
    .split(',')
    .map((m) => m.trim().toLowerCase())
    .filter(Boolean)
}

export function isEmailInSharedPortfolioMembers(
  members: string | null | undefined,
  email: string | null | undefined,
): boolean {
  const normalizedEmail = email?.trim().toLowerCase()
  if (!normalizedEmail) return false
  return parseSharedPortfolioMemberEmails(members).includes(normalizedEmail)
}

/** Shared portfolio rows without member metadata are treated as accessible (search is already scoped). */
export function isAccessibleSharedPortfolioSolution(
  members: string | null | undefined,
  email: string | null | undefined,
): boolean {
  return !members || isEmailInSharedPortfolioMembers(members, email)
}

export type FollowPortfolioEntry = {
  portfolioId?: string | null
  followingType?: string | null
}

export function resolveFollowTypeLabel(entries: FollowPortfolioEntry[], privateFollowingType?: string | null): string {
  const hasPrivate = entries.some((entry) => entry.portfolioId == null)
  const sharedEntries = entries.filter((entry) => entry.portfolioId != null)

  if (hasPrivate && sharedEntries.length > 0) {
    return 'Following'
  }
  if (hasPrivate) {
    const privateEntry = entries.find((entry) => entry.portfolioId == null)
    return privateFollowingType || privateEntry?.followingType || 'Portfolio'
  }
  if (sharedEntries.length > 1) {
    return 'Following'
  }
  if (sharedEntries.length === 1) {
    return sharedEntries[0]?.followingType || 'Portfolio'
  }
  return ''
}

export function resolveFollowStateFromPortfolioEntries(entries: FollowPortfolioEntry[]): {
  isFollowed: boolean
  label: string
} {
  const privateFollowingType = entries.find((entry) => entry.portfolioId == null)?.followingType

  return {
    isFollowed: entries.length > 0,
    label: resolveFollowTypeLabel(entries, privateFollowingType),
  }
}

/** Label for follow button: single portfolio → category/type, multiple → "Following", none → "". */
export function resolveSolutionFollowTypeLabel(
  privateFollowingType: string | null | undefined,
  sharedCategories: Array<string | null | undefined>,
): string {
  const hasPrivate = Boolean(privateFollowingType)
  const sharedCount = sharedCategories.length

  if (hasPrivate && sharedCount > 0) {
    return 'Following'
  }
  if (hasPrivate) {
    return privateFollowingType || ''
  }
  if (sharedCount > 1) {
    return 'Following'
  }
  if (sharedCount === 1) {
    return sharedCategories[0] || ''
  }
  return ''
}

export function hasSolutionPortfolioFollow(
  privateFollowingType: string | null | undefined,
  sharedCategories: Array<string | null | undefined>,
): boolean {
  return Boolean(privateFollowingType) || sharedCategories.length > 0
}
